import { ReactNode, Suspense, useEffect, useState } from 'react';

import {
  RiDeleteBinLine,
  RiDownloadLine,
  RiExternalLinkLine,
  RiFileCopyLine,
  RiFileLine,
  RiMore2Fill,
  RiPencilLine,
} from '@remixicon/react';

import { useRouteRef } from '@backstage/core-plugin-api';
import {
  ButtonIcon,
  Flex,
  Menu,
  MenuItem,
  MenuTrigger,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';

import {
  DateUtils,
  DialogComposite,
  ErrorBoundary,
  Spinner,
  downloadTextFile,
  sanitizeFilename,
  useAlert,
  useDialog,
} from '@spotify/confidence-core-react';
import {
  CollaborativeDocumentProvider,
  CommentDecorationsExtension,
  CommentMark,
  CopyAsMarkdownButton,
  TipTapEditor,
  useCollaborativeDocument,
  useCollaborativeDocumentContext,
} from '@spotify/confidence-editor-react';
import { AttachmentFragment, getTypeOrNull } from '@spotify/confidence-graphql';
import { MarkdownContent } from '@spotify/plugin-confidence-chat-react';
import {
  IdentityChip,
  identityKind,
} from '@spotify/plugin-confidence-iam-react';
import {
  ArtifactCardBase,
  ArtifactIcon,
  ArtifactListItem,
  createArtifactExtension,
  getResourceName,
  isExternalLink,
} from '@spotify/plugin-confidence-projects-react';
import {
  MetricCard as ReportMetricCard,
  MetricTable as ReportMetricTable,
  VariantCard as ReportVariantCard,
} from '@spotify/plugin-confidence-workflows-module-report';

import { projectsPlugin } from '../plugin';
import { collaborativeDocumentRouteRef } from '../routes';
import styles from './DocumentArtifactExtension.module.css';

const reflowSingleLineTables = (markdown: string): string =>
  markdown.replace(/\|(?:[^|\n]*\|)+/g, table => {
    if (table.includes('\n')) return table;
    const separator = table.match(/\|(?::?-+:?\|)+/);
    if (!separator) return table;
    const cols = (separator[0].match(/:?-+:?/g) ?? []).length;
    if (cols === 0) return table;

    const pipesPerRow = cols + 1;
    const rows: string[] = [];
    let rowStart = 0;
    let rowPipes = 0;
    for (let i = 0; i < table.length; i++) {
      if (table[i] !== '|') continue;
      rowPipes++;
      if (rowPipes === pipesPerRow) {
        rows.push(table.slice(rowStart, i + 1).trim());
        rowStart = i + 1;
        rowPipes = 0;
      }
    }
    if (rows.length < 2) return table;
    return `\n\n${rows.join('\n')}\n\n`;
  });

const hasDocumentContent = (content: unknown): boolean =>
  (typeof content === 'string' && content.trim().length > 0) ||
  (content !== null && content !== undefined && typeof content === 'object');

const DocumentArtifactContent = ({
  documentName,
}: {
  documentName: string;
}) => {
  const { collaborativeDocument, isLoading, startPolling, stopPolling } =
    useCollaborativeDocument({
      name: documentName,
    });

  const markdown = collaborativeDocument?.markdown;
  const content = collaborativeDocument?.content;
  const hasContent = hasDocumentContent(content);
  const hasPendingOperation = !!collaborativeDocument?.pendingOperation;
  const isUpdating = hasPendingOperation || (hasContent && !markdown);

  useEffect(() => {
    if (isUpdating) {
      startPolling(2000);
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [isUpdating, startPolling, stopPolling]);

  if (isLoading && !collaborativeDocument) {
    return (
      <div className={styles.loading}>
        <Spinner size={24} aria-label="Loading document" />
      </div>
    );
  }

  if (markdown) {
    return (
      <MarkdownContent>{reflowSingleLineTables(markdown)}</MarkdownContent>
    );
  }

  if (typeof content === 'string' && content.trim()) {
    return <MarkdownContent>{reflowSingleLineTables(content)}</MarkdownContent>;
  }

  if (isUpdating) {
    return (
      <div className={styles.loading}>
        <Spinner size={24} aria-label="Loading document" />
      </div>
    );
  }

  return (
    <div className={styles.emptyState}>
      <Text variant="body-small" color="secondary">
        This document is empty.
      </Text>
    </div>
  );
};

const MetricCard = ReportMetricCard.extend({
  addNodeView() {
    return null;
  },
});

const MetricTable = ReportMetricTable.extend({
  addNodeView() {
    return null;
  },
});

const VariantCard = ReportVariantCard.extend({
  addNodeView() {
    return null;
  },
});

const PREVIEW_EDITOR_EXTENSIONS = [
  CommentMark,
  CommentDecorationsExtension,
  MetricCard,
  MetricTable,
  VariantCard,
];

const DocumentPreviewEditorInner = () => {
  const { collaborativeDocument, isPublished } =
    useCollaborativeDocumentContext();
  const updater = getTypeOrNull(collaborativeDocument?.updater);
  const hasHumanUpdater = updater
    ? identityKind(updater.name) === 'user'
    : false;

  return (
    <Flex direction="column" gap="4">
      {collaborativeDocument?.updateTime && (
        <Flex align="center" gap="2">
          <Text variant="body-small" color="secondary">
            Last edited{' '}
            {DateUtils.xAgo(new Date(collaborativeDocument.updateTime))}
          </Text>
          {hasHumanUpdater && updater && (
            <IdentityChip size="small" identityRef={updater} />
          )}
        </Flex>
      )}
      <TipTapEditor
        readonly={isPublished}
        disableToolbar
        extensions={PREVIEW_EDITOR_EXTENSIONS}
      />
    </Flex>
  );
};

const DocumentPreviewEditor = ({
  documentName,
  renderActions,
}: {
  documentName: string;
  renderActions?: (documentName: string) => ReactNode;
}) => {
  return (
    <ErrorBoundary
      renderFallback={() => (
        <DocumentArtifactContent documentName={documentName} />
      )}
    >
      <Suspense
        fallback={
          <div className={styles.loading}>
            <Spinner size={24} />
          </div>
        }
      >
        <CollaborativeDocumentProvider documentName={documentName}>
          {renderActions?.(documentName)}
          <DocumentPreviewEditorInner />
        </CollaborativeDocumentProvider>
      </Suspense>
    </ErrorBoundary>
  );
};

const isDocumentArtifact = (artifact: AttachmentFragment) =>
  !isExternalLink(artifact.uri) &&
  getResourceName(artifact.uri).startsWith('collaborativeDocuments/');

const useDocumentHref = (documentName: string) => {
  const collaborativeDocumentRoute = useRouteRef(collaborativeDocumentRouteRef);
  const [, documentId] = documentName.split('/');
  return collaborativeDocumentRoute?.({ id: documentId });
};

const DocumentArtifactListItem = ({
  artifact,
  icon,
  onSelect,
  onDelete,
  onRename,
}: {
  artifact: AttachmentFragment;
  icon: ReactNode;
  onSelect: () => void;
  onDelete?: () => void;
  onRename?: () => void;
}) => {
  const alert = useAlert();
  const [menuOpened, setMenuOpened] = useState(false);
  const documentName = getResourceName(artifact.uri);
  const documentHref = useDocumentHref(documentName);
  const { collaborativeDocument } = useCollaborativeDocument({
    name: documentName,
    skip: !menuOpened,
  });

  const dateLabel = new Date(artifact.createTime).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  const markdownContent =
    collaborativeDocument?.markdown ??
    (typeof collaborativeDocument?.content === 'string'
      ? collaborativeDocument.content
      : undefined);

  const handleCopyMarkdown = () => {
    if (!markdownContent) return;
    navigator.clipboard.writeText(markdownContent).then(
      () =>
        alert.post({
          message: 'Copied to clipboard',
          severity: 'success',
          display: 'transient',
        }),
      () => alert.post({ message: 'Failed to copy', severity: 'error' }),
    );
  };

  const handleDownloadMarkdown = () => {
    if (!markdownContent) return;
    const displayName = artifact.displayName || 'document';
    const filename = `${sanitizeFilename(displayName)}.md`;
    downloadTextFile(markdownContent, filename);
  };

  return (
    <ArtifactListItem.Root onClick={onSelect}>
      <ArtifactListItem.Icon>
        <ArtifactIcon>{icon}</ArtifactIcon>
      </ArtifactListItem.Icon>
      <ArtifactListItem.Content
        primary={
          <Text variant="body-medium" as="div" truncate>
            {artifact.displayName || 'Untitled document'}
          </Text>
        }
        secondary={
          <Text variant="body-x-small" as="div" color="secondary" truncate>
            Document &middot; {dateLabel}
          </Text>
        }
      />
      <ArtifactListItem.Action>
        <MenuTrigger onOpenChange={open => open && setMenuOpened(true)}>
          <ButtonIcon
            variant="tertiary"
            size="small"
            icon={<RiMore2Fill size={16} />}
            aria-label="Document actions"
          />
          <Menu placement="bottom end">
            <MenuItem
              iconStart={<RiFileLine />}
              onAction={() =>
                documentHref ? window.open(documentHref, '_blank') : onSelect()
              }
            >
              Open full document
            </MenuItem>
            <MenuItem
              iconStart={<RiFileCopyLine />}
              onAction={handleCopyMarkdown}
              isDisabled={!markdownContent}
            >
              Copy as Markdown
            </MenuItem>
            <MenuItem
              iconStart={<RiDownloadLine />}
              onAction={handleDownloadMarkdown}
              isDisabled={!markdownContent}
            >
              Download as Markdown
            </MenuItem>
            {onRename && (
              <MenuItem iconStart={<RiPencilLine />} onAction={onRename}>
                Rename
              </MenuItem>
            )}
            {onDelete && (
              <MenuItem
                color="danger"
                iconStart={<RiDeleteBinLine />}
                onAction={onDelete}
              >
                Delete
              </MenuItem>
            )}
          </Menu>
        </MenuTrigger>
      </ArtifactListItem.Action>
    </ArtifactListItem.Root>
  );
};

const PopOutDocumentButton = ({ documentName }: { documentName: string }) => {
  const documentHref = useDocumentHref(documentName);

  if (!documentHref) {
    return null;
  }

  return (
    <TooltipTrigger>
      <ButtonIcon
        variant="tertiary"
        size="small"
        icon={<RiExternalLinkLine size={16} />}
        onPress={() => window.open(documentHref, '_blank')}
        aria-label="Pop out document"
      />
      <Tooltip>Open full document</Tooltip>
    </TooltipTrigger>
  );
};

const DocumentArtifactHeaderActions = ({
  artifact,
}: {
  artifact: AttachmentFragment;
}) => <PopOutDocumentButton documentName={getResourceName(artifact.uri)} />;

const DocumentDialogActions = ({ documentName }: { documentName: string }) => {
  return (
    <div className={styles.dialogActions}>
      <PopOutDocumentButton documentName={documentName} />
      <CopyAsMarkdownButton />
    </div>
  );
};

const DocumentArtifactCard = ({
  artifact,
  onDelete,
}: {
  artifact: AttachmentFragment;
  onDelete?: () => void;
}) => {
  const { openDialog, closeDialog } = useDialog();
  const documentName = getResourceName(artifact.uri);

  const handleNavigate = () => {
    openDialog({
      content: (
        <DialogComposite.Root static onClose={closeDialog}>
          <DialogComposite.Content>
            <DialogComposite.CloseButton />
            <DialogComposite.Header>
              <DialogComposite.Title>
                {artifact.displayName || 'Untitled document'}
              </DialogComposite.Title>
            </DialogComposite.Header>
            <DialogComposite.Body className={styles.dialogBody}>
              <DocumentPreviewEditor
                documentName={documentName}
                renderActions={name => (
                  <DocumentDialogActions documentName={name} />
                )}
              />
            </DialogComposite.Body>
          </DialogComposite.Content>
        </DialogComposite.Root>
      ),
      dialogProps: { maxWidth: 'md', fullWidth: true },
    });
  };

  return (
    <ArtifactCardBase
      artifact={artifact}
      onNavigate={handleNavigate}
      onDelete={onDelete}
    />
  );
};

export const DocumentArtifactExtension = projectsPlugin.provide(
  createArtifactExtension({
    id: 'document',
    predicate: isDocumentArtifact,
    icon: () => ({ Icon: RiFileLine, color: '#6366F1' }),
    label: 'Document',
    category: 'Documents',
    render: artifact => (
      <DocumentPreviewEditor documentName={getResourceName(artifact.uri)} />
    ),
    renderHeaderActions: artifact => (
      <DocumentArtifactHeaderActions artifact={artifact} />
    ),
    renderListItem: (artifact, { onSelect, onDelete, onRename, icon }) => (
      <DocumentArtifactListItem
        artifact={artifact}
        icon={icon}
        onSelect={onSelect}
        onDelete={onDelete}
        onRename={onRename}
      />
    ),
    renderCard: (artifact, { onDelete }) => (
      <DocumentArtifactCard artifact={artifact} onDelete={onDelete} />
    ),
  }),
);
